import { orders, todaysTasks } from '@/mocks/orders';
import SummaryCard from './components/SummaryCard';
import OrderRow from './components/OrderRow';
import ActivityLog from './components/ActivityLog';

export default function Dashboard() {
  const totalNewToday = orders.filter(o => o.status === 'yeni' && o.orderDate === '2026-06-17').length;
  const inProduction = orders.filter(o => !['teslim', 'iptal', 'kargoya_hazir', 'kargoda'].includes(o.status)).length;
  const inDrawing = orders.filter(o => ['cizimde', 'cizim_onay'].includes(o.status)).length;
  const inCutting = orders.filter(o => o.status === 'kesimde').length;
  const inLighting = orders.filter(o => ['aydinlatma', 'test'].includes(o.status)).length;
  const readyForCargo = orders.filter(o => o.status === 'kargoya_hazir').length;
  const inCargo = orders.filter(o => o.status === 'kargoda').length;
  const delivered = orders.filter(o => o.status === 'teslim').length;

  const pendingPayment = orders
    .filter(o => o.paymentType === 'kapida_odeme' && o.paymentStatus === 'bekleniyor')
    .reduce((sum, o) => sum + o.amount, 0);
  const collectedTotal = orders
    .filter(o => o.paymentStatus === 'tahsil_edildi')
    .reduce((sum, o) => sum + o.amount, 0);

  const lateOrders = orders.filter(o => o.isLate);
  const urgentOrders = orders.filter(o => o.isUrgent && o.status !== 'teslim');
  const cargoWaitingOrders = orders.filter(o => o.status === 'kargoya_hazir');

  const summaryCards = [
    { label: 'Bugünkü Yeni Siparişler', value: totalNewToday, icon: 'ri-add-circle-line', colorClass: 'bg-status-blue/10 text-status-blue' },
    { label: 'Üretimdeki Siparişler', value: inProduction, icon: 'ri-tools-line', colorClass: 'bg-status-purple/10 text-status-purple' },
    { label: 'Çizim Aşamasındaki İşler', value: inDrawing, icon: 'ri-pencil-ruler-line', colorClass: 'bg-status-purple/10 text-status-purple' },
    { label: 'Kesim Aşamasındaki İşler', value: inCutting, icon: 'ri-scissors-cut-line', colorClass: 'bg-status-orange/10 text-status-orange' },
    { label: 'Aydınlatma / Test', value: inLighting, icon: 'ri-lightbulb-line', colorClass: 'bg-status-yellow/10 text-status-yellow' },
    { label: 'Kargoya Hazır', value: readyForCargo, icon: 'ri-box-3-line', colorClass: 'bg-status-navy/10 text-status-navy' },
    { label: 'Kargoda Olan', value: inCargo, icon: 'ri-truck-line', colorClass: 'bg-status-sky/10 text-status-sky' },
    { label: 'Teslim Edilen', value: delivered, icon: 'ri-check-double-line', colorClass: 'bg-status-green/10 text-status-green' },
    { label: 'Bekleyen Kapıda Ödeme', value: pendingPayment.toLocaleString('tr-TR'), icon: 'ri-money-dollar-circle-line', colorClass: 'bg-status-orange/10 text-status-orange', suffix: '₺' },
    { label: 'Tahsil Edilen Toplam', value: collectedTotal.toLocaleString('tr-TR'), icon: 'ri-bank-card-line', colorClass: 'bg-status-green/10 text-status-green', suffix: '₺' },
    { label: 'Geciken İşler', value: lateOrders.length, icon: 'ri-alert-line', colorClass: 'bg-status-red/10 text-status-red' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-stone-900">Dashboard</h2>
          <p className="text-sm text-stone-500 mt-0.5">17 Haziran 2026, Salı · Günlük operasyon özeti</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="flex items-center gap-2 px-4 py-2 bg-gold-500 text-white text-sm font-medium rounded-lg hover:bg-gold-600 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-add-line" />
            Yeni Sipariş Ekle
          </button>
        </div>
      </div>

      {/* Summary Cards Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 md:gap-4 mb-6">
        {summaryCards.map((card, index) => (
          <SummaryCard key={index} {...card} />
        ))}
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Left: Today's Tasks & Urgent Orders */}
        <div className="lg:col-span-2 space-y-4 md:space-y-6">
          {/* Today's Tasks */}
          <div className="bg-white rounded-xl border border-stone-200/80 overflow-hidden">
            <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-stone-800">Bugün Yapılacak İşler</h3>
              <span className="text-xs text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full">{todaysTasks.length} görev</span>
            </div>
            <div className="divide-y divide-stone-50">
              {todaysTasks.map((task) => {
                const taskOrder = orders.find(o => o.id === task.orderId);
                return (
                  <div key={task.id} className="flex items-center gap-3 px-5 py-3">
                    <input type="checkbox" className="w-4 h-4 rounded border-stone-300 text-gold-500 focus:ring-gold-400 cursor-pointer" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-stone-700">{task.task}</p>
                      <p className="text-xs text-stone-400 mt-0.5">
                        {task.orderId} · {taskOrder?.customerName}
                      </p>
                    </div>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${
                      task.priority === 'high' 
                        ? 'bg-status-red/10 text-status-red' 
                        : 'bg-status-yellow/10 text-status-yellow'
                    }`}>
                      {task.priority === 'high' ? 'Yüksek' : 'Orta'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Urgent Orders */}
          <div className="bg-white rounded-xl border border-stone-200/80 overflow-hidden">
            <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-stone-800">Acil Siparişler</h3>
              <span className="text-xs text-status-red bg-status-red/10 px-2 py-0.5 rounded-full">{urgentOrders.length} sipariş</span>
            </div>
            <div className="divide-y divide-stone-50">
              {urgentOrders.map((order) => (
                <OrderRow key={order.id} order={order} />
              ))}
            </div>
          </div>

          {/* Cargo Waiting */}
          {cargoWaitingOrders.length > 0 && (
            <div className="bg-white rounded-xl border border-stone-200/80 overflow-hidden">
              <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-stone-800">Kargo Bekleyen Siparişler</h3>
                <span className="text-xs text-status-navy bg-status-navy/10 px-2 py-0.5 rounded-full">{cargoWaitingOrders.length} sipariş</span>
              </div>
              <div className="divide-y divide-stone-50">
                {cargoWaitingOrders.map((order) => (
                  <OrderRow key={order.id} order={order} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: Activity Log & Stats */}
        <div className="space-y-4 md:space-y-6">
          <ActivityLog />

          {/* Quick Stats */}
          <div className="bg-white rounded-xl border border-stone-200/80 overflow-hidden">
            <div className="px-5 py-4 border-b border-stone-100">
              <h3 className="text-sm font-semibold text-stone-800">Hızlı Özet</h3>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-stone-500">Toplam Aktif Sipariş</span>
                <span className="text-sm font-bold text-stone-800">{orders.filter(o => o.status !== 'teslim' && o.status !== 'iptal').length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-stone-500">Bugün Tahsil Edilen</span>
                <span className="text-sm font-bold text-status-green">1.800 ₺</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-stone-500">Bu Ay Sipariş</span>
                <span className="text-sm font-bold text-stone-800">{orders.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-stone-500">Bu Ay Ciro</span>
                <span className="text-sm font-bold text-gold-600">{orders.reduce((sum, o) => sum + o.amount, 0).toLocaleString('tr-TR')} ₺</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-stone-500">Müşteri Memnuniyeti</span>
                <span className="text-sm font-bold text-status-green">%96</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}